import { Box, Button, DialogContent, Typography } from '@mui/material';
import Image from 'next/image';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { replacePathParams } from 'utils/routes.utils';

import { Routes } from 'constants/routes.constants';
import { useAppRedirect } from 'hooks/useAppRedirect';
import { useGenericClick, useOriginClick } from 'services/datalayer/browse';
import { TicketsPushAppField } from 'types/api/dato-extensions.types';

import AppStoresWithQR from 'components/@common/AppStoresWithQR';
import ResponsiveDrawerDialog from 'components/@common/ResponsiveDrawerDialog';

interface Props {
  open: boolean;
  onClose: () => void;
  onSoftCtaClick: () => void;
  variant: TicketsPushAppField;
  eventSlug: string;
}

const PushAppDialog = ({
  open,
  eventSlug,
  variant,
  onClose,
  onSoftCtaClick,
}: Props) => {
  const { onRedirect } = useAppRedirect();
  const handleClick = useOriginClick('push ticket sales to app', 'Tickets');
  const handleGenericClick = useGenericClick();

  const onClickBuyTickets = () => {
    handleGenericClick({ clickItem: 'buy tickets in app' });
    onRedirect(
      replacePathParams(Routes.EventCart, {
        slug: eventSlug,
      }),
    );
  };

  const handleSoftCtaClick = () => {
    handleGenericClick({ clickItem: 'download app later' });
    onSoftCtaClick();
  };

  useEffect(() => {
    if (open) handleClick();
  }, [open, handleClick]);

  return (
    <ResponsiveDrawerDialog isOpen={open} onClose={onClose}>
      <DialogContent sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: { md: 360 },
          }}
        >
          <Box sx={{ position: 'relative', height: 242 }}>
            <Image
              src="/images/tickets_push_app_illustration.svg"
              alt="Ticket inside app illustration"
              fill
              unoptimized
            />
          </Box>
          <Typography
            variant="h4"
            color="secondary"
            sx={[
              {
                textTransform: 'uppercase',
                mt: { xs: 4, md: 5 },
              },
              (theme) => ({
                textAlign: 'center',
                [theme.breakpoints.up('md')]: {
                  fontSize: theme.typography.h5.fontSize,
                },
              }),
            ]}
          >
            <FormattedMessage id="tickets.app.push.title" />
          </Typography>
          <Typography
            variant="body2"
            sx={[
              {
                mt: { xs: 3, md: 2 },
              },
              (theme) => ({
                textAlign: 'center',
                [theme.breakpoints.up('md')]: {
                  fontSize: theme.typography.body1.fontSize,
                },
              }),
            ]}
          >
            <FormattedMessage id="tickets.app.push.description" />
          </Typography>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={onClickBuyTickets}
            sx={{ mt: 5, display: { md: 'none' } }}
          >
            <FormattedMessage id="tickets.app.push.cta" />
          </Button>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              bgcolor: 'global01.800',
              p: 3,
              alignItems: 'center',
              borderRadius: 4,
              mt: 3,
            }}
          >
            <Typography
              variant="h7"
              color="secondary"
              sx={{
                textTransform: 'uppercase',
                mb: 2,
                textAlign: 'center',
              }}
            >
              <FormattedMessage id="tickets.app.push.cta" />
            </Typography>
            <AppStoresWithQR
              onClick={() =>
                handleGenericClick({ clickItem: 'buy tickets in app' })
              }
            />
          </Box>
          {variant === 'soft' && (
            <Button sx={{ mt: 3 }} onClick={handleSoftCtaClick}>
              <FormattedMessage id="tickets.app.push.soft.cta" />
            </Button>
          )}
        </Box>
      </DialogContent>
    </ResponsiveDrawerDialog>
  );
};

export default PushAppDialog;
